import { ReportingBuildingService } from '@http/clients/reporting-app/reporting-building.service';
import { TabNames } from '@models/enums/TabNames';
import { GridDisplayScheme, OverviewLayout } from '@pages/reports/types/report-layout-type.enum';
import { WidgetType } from '@pages/reports/types/report-widget-type.enum';
import { formatNumbersCompact, truncateValue } from '@utils/format-functions';
import { AgChartLegendLabelFormatterParams } from 'ag-charts-community';
import { ICellRendererParams } from 'ag-grid-enterprise';
import { IReportConfiguration } from '../../types/report-config.interface';
import { ReportType } from '../../types/report-type.enum';
import {
    accountEditorConfig,
    buildingClassEditorConfig,
    companyPropertyManagerEditorConfig,
    customerEditorConfig,
    departmentEditorConfig,
    includeArchivedEditorConfig,
    includeOpportunityArchivedEditorConfig,
    marketsEditorConfig,
    opportunityDueDateEditorConfig,
    opportunitySourceEditorConfig,
    projectEndDateEditorConfig,
    projectStartDateEditorConfig,
    salesPersonsEditorConfig,
    stateEditorConfig
} from './shared/additional-filters';
import { crmMainFilters } from './shared/main-filters';

export const Buildings: IReportConfiguration<ReportingBuildingService> = {
    base: {
        type: ReportType.Buildings,
        serviceType: ReportingBuildingService
    },
    overviewTab: {
        widgets: [
            {
                methodName: 'getBuildingBoxesValues',
                type: WidgetType.Box,
                layoutTemplateArea: 'boxes'
            },
            {
                methodName: 'getBuildingMetricsValues',
                type: WidgetType.Metrics,
                layoutTemplateArea: 'metrics',
                settings: {
                    cols: [
                        { name: 'avg', label: 'Average' },
                        { name: 'min', label: 'Min' },
                        { name: 'med', label: 'Med' },
                        { name: 'max', label: 'Max' }
                    ],
                    rows: [
                        { name: 'BuildingSqFt', label: 'Building Sq. Ft.' },
                        { name: 'AwardedAmountPerSqFt', label: 'Awarded Amount / Sq. Ft.' }
                    ]
                }
            },
            {
                methodName: 'getBuildingLineChartValues',
                type: WidgetType.Chart,
                layoutTemplateArea: 'chart',
                settings: {
                    title: {
                        text: 'Top 10 Buildings by Awarded Amount'
                    },
                    series: [
                        {
                            type: 'line',
                            xKey: 'buildingName',
                            yKey: 'awardedAmount',
                            yName: 'Awarded Amount ($)'
                        },
                        {
                            type: 'line',
                            xKey: 'buildingName',
                            yKey: 'avgAwardedAmountPerSqFt',
                            yName: 'Awarded Amount / Sq. Ft.'
                        }
                    ],
                    axes: [
                        {
                            type: 'category',
                            position: 'bottom',
                            label: {
                                formatter: (params: AgChartLegendLabelFormatterParams) =>
                                    truncateValue(params.value, 15),
                                rotation: 315
                            }
                        },
                        {
                            type: 'number',
                            position: 'left',
                            title: {
                                text: 'Amount'
                            },
                            keys: ['awardedAmount'],
                            label: {
                                formatter: (params: AgChartLegendLabelFormatterParams) =>
                                    formatNumbersCompact(params.value)
                            }
                        },
                        {
                            type: 'number',
                            position: 'right',
                            title: {
                                text: 'Amount / Sq. Ft.'
                            },
                            keys: ['avgAwardedAmountPerSqFt']
                        }
                    ]
                }
            },
            {
                methodName: 'getBuildingColumnChartValues',
                type: WidgetType.Chart,
                layoutTemplateArea: 'chart2',
                settings: {
                    title: {
                        text: 'Salesperson Performance'
                    },
                    series: [
                        {
                            type: 'bar',
                            xKey: 'salesPerson',
                            yKey: 'avgAwardedCountPerBuilding',
                            yName: 'Awarded Count per Building'
                        },
                        {
                            type: 'line',
                            xKey: 'salesPerson',
                            yKey: 'avgAwardedAmountPerSqFt',
                            yName: 'Awarded Amount / Sq. Ft.'
                        }
                    ],
                    axes: [
                        {
                            type: 'category',
                            position: 'bottom',
                            label: {
                                formatter: (params: AgChartLegendLabelFormatterParams) =>
                                    truncateValue(params.value, 15),
                                rotation: 315
                            }
                        },
                        {
                            type: 'number',
                            position: 'left',
                            title: {
                                text: 'Awarded Count per Building'
                            },
                            keys: ['avgAwardedCountPerBuilding']
                        },
                        {
                            type: 'number',
                            position: 'right',
                            title: {
                                text: 'Amount / Sq. Ft.'
                            },
                            keys: ['avgAwardedAmountPerSqFt']
                        }
                    ]
                }
            }
        ],
        layout: OverviewLayout.ExtendedTwoPlusOne
    },
    gridTab: {
        widgets: [
            {
                methodName: 'getBuildingValues',
                type: WidgetType.Grid,
                layoutTemplateArea: 'grid',
                settings: [
                    {
                        field: 'entityId',
                        headerName: 'Building ID',
                        lockVisible: true,
                        enableRowGroup: true,
                        cellRenderer: (params: ICellRendererParams) => {
                            if (params.value) {
                                const link = `/buildings-single/${params.value}/${TabNames[TabNames.Overview]}`;
                                return `<a class="anchor" href="${link}" target="_blank" rel="noopener noreferrer">${params.value}</a>`;
                            }
                            return `<div></div>`;
                        }
                    },
                    { field: 'buildingName', headerName: 'Building Name', enableRowGroup: true, lockVisible: true },
                    { field: 'alternateName', headerName: 'Alternate Name (comma separated)', enableRowGroup: true },
                    { field: 'buildingContact', headerName: 'Property Manager', enableRowGroup: true },
                    { field: 'address', headerName: 'Full Address', enableRowGroup: true },
                    { field: 'buildingClass', headerName: 'Class', enableRowGroup: true },
                    { field: 'numberOfFloors', headerName: 'Number Of Floors', enableRowGroup: true },
                    { field: 'size', headerName: 'Building Sq. Ft.' },
                    { field: 'opportunityQty', headerName: 'Opportunity Qty (Main Building)', aggFunc: 'sum' },
                    { field: 'salesPersons', headerName: 'Salespersons', enableRowGroup: true },
                    { field: 'awardedCount', headerName: 'Awarded Count', aggFunc: 'sum' },
                    { field: 'awardedAmount', headerName: 'Awarded Amount', type: 'currency', aggFunc: 'sum' },
                    { field: 'awardedAmountPerSqFt', headerName: 'Awarded Amount / Sq. Ft.' },
                    { field: 'organization', headerName: 'Organization', enableRowGroup: true },
                    { field: 'condition', headerName: 'Condition', enableRowGroup: true },
                    {
                        field: 'buildingLastUpdatedNote',
                        headerName: 'Last Updated Note',
                        tooltipField: 'buildingLastUpdatedNote'
                    },
                    { field: 'modificationDate', headerName: 'Modification Date', type: 'date' }
                ]
            }
        ],
        layout: GridDisplayScheme.Standard
    },
    filters: {
        main: [...crmMainFilters],
        additional: [
            opportunityDueDateEditorConfig,
            projectStartDateEditorConfig,
            projectEndDateEditorConfig,
            marketsEditorConfig,
            opportunitySourceEditorConfig,
            departmentEditorConfig,
            customerEditorConfig,
            companyPropertyManagerEditorConfig,
            accountEditorConfig,
            stateEditorConfig,
            buildingClassEditorConfig,
            salesPersonsEditorConfig,
            includeOpportunityArchivedEditorConfig,
            includeArchivedEditorConfig
        ]
    }
};
